import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * Orca agent CLI discriminator — which coding agent to use.
 * @since 1.0.0
 */
export const OrcaAgentCli = Schema.Literal("claude", "codex");
export type OrcaAgentCli = Schema.Schema.Type<typeof OrcaAgentCli>;

/**
 * Orca task phase — lifecycle states for a unit of work.
 * @since 1.0.0
 */
export const OrcaTaskPhase = Schema.Literal(
  "BUILDING",
  "CI_CHECKING",
  "COMPLETENESS_CHECKING",
  "REVIEW_CHECKING",
  "FIXING",
  "MERGING",
  "PASSED",
  "FAILED",
  "CANCELED_BY_GRAPH_CHANGE",
  "CANCELED_IN_LINEAR",
  // INF-526: human-tombstone terminals (see orcaTaskPhaseEnum in
  // src/tables/orca-tasks.ts). Neither PASSED nor FAILED in metrics.
  "SUPERSEDED",
  "HOLD",
);
export type OrcaTaskPhase = Schema.Schema.Type<typeof OrcaTaskPhase>;

/**
 * The set of strictly-terminal orca task phases — a row in any of these will
 * not advance further and consumes no in-flight / parallel slot.
 *
 * - `PASSED` / `FAILED`: agent outcome terminals.
 * - `CANCELED_BY_GRAPH_CHANGE`: transient tombstone the dispatcher recycles.
 * - `CANCELED_IN_LINEAR` / `SUPERSEDED` / `HOLD`: human tombstones (INF-526)
 *   that are NOT agent failures and must never be counted as PASSED or FAILED
 *   in any success/pass-rate aggregation.
 *
 * Single source of truth for the terminal-phase filters scattered across the
 * accessors (`countNonTerminalForProject`, `markCanceled*`), the scheduler
 * (`isInProgress`), and the container reaper (`TERMINAL_PHASES`).
 *
 * @since 1.0.0
 */
export const TERMINAL_ORCA_TASK_PHASES = [
  "PASSED",
  "FAILED",
  "CANCELED_BY_GRAPH_CHANGE",
  "CANCELED_IN_LINEAR",
  "SUPERSEDED",
  "HOLD",
] as const satisfies ReadonlyArray<OrcaTaskPhase>;

/**
 * True when `phase` is one of {@link TERMINAL_ORCA_TASK_PHASES}.
 *
 * @since 1.0.0
 */
export const isTerminalOrcaTaskPhase = (phase: OrcaTaskPhase | string): boolean =>
  (TERMINAL_ORCA_TASK_PHASES as ReadonlyArray<string>).includes(phase);

/**
 * Orca task gate — which gate caused a failure/fix cycle.
 * @since 1.0.0
 */
export const OrcaTaskGate = Schema.Literal("ci", "completeness", "review", "merge");
export type OrcaTaskGate = Schema.Schema.Type<typeof OrcaTaskGate>;

/**
 * Schema for a single attempt history entry.
 *
 * `verdictKey` (OL-141) is a stable hash of the gate verdict that triggered
 * this attempt, so repeated identical failures group without parsing
 * `failureContext`. It is OPTIONAL: every entry written before OL-143 wires
 * the writes has no such key and must still decode.
 *
 * @since 1.0.0
 */
export const AttemptHistoryEntry = Schema.Struct({
  attempt: Schema.Number,
  gate: Schema.String,
  failureContext: Schema.NullOr(Schema.String),
  commitSha: Schema.optional(Schema.String),
  verdictKey: Schema.optional(Schema.String),
  createdAt: Schema.String,
});
export type AttemptHistoryEntry = Schema.Schema.Type<typeof AttemptHistoryEntry>;

/**
 * Append-only history of meaningful state events on an orca_task row
 * (INF-521). Each entry is a discriminated union by `kind` and stamps an
 * ISO-8601 `ts`. The dispatcher, state machine, reconciler, webhook
 * handlers, container reaper, and Linear-state push helper all paired-write
 * one entry per event so a single `select history from orca_tasks where
 * linear_issue_identifier = X` reproduces the full forensic timeline that
 * today requires grep-piping api.log against GitHub + Linear webhooks.
 *
 * @since 1.0.0
 */
export const PhaseTransitionHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("phase_transition"),
  from: Schema.NullOr(OrcaTaskPhase),
  to: OrcaTaskPhase,
  reason: Schema.NullOr(Schema.String),
  gate: Schema.NullOr(OrcaTaskGate),
  attempt: Schema.Number,
});
export type PhaseTransitionHistoryEvent = Schema.Schema.Type<typeof PhaseTransitionHistoryEvent>;

export const WorkflowDispatchHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("workflow_dispatch"),
  workflowFile: Schema.String,
  runId: Schema.NullOr(Schema.Number),
  correlationId: Schema.NullOr(Schema.String),
});
export type WorkflowDispatchHistoryEvent = Schema.Schema.Type<typeof WorkflowDispatchHistoryEvent>;

export const WorkflowCompletedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("workflow_completed"),
  runId: Schema.Number,
  conclusion: Schema.NullOr(Schema.String),
  durationMs: Schema.NullOr(Schema.Number),
});
export type WorkflowCompletedHistoryEvent = Schema.Schema.Type<typeof WorkflowCompletedHistoryEvent>;

export const CheckRunHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("check_run"),
  name: Schema.String,
  status: Schema.String,
  conclusion: Schema.NullOr(Schema.String),
});
export type CheckRunHistoryEvent = Schema.Schema.Type<typeof CheckRunHistoryEvent>;

export const PrCreatedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("pr_created"),
  prNumber: Schema.Number,
  headRef: Schema.String,
});
export type PrCreatedHistoryEvent = Schema.Schema.Type<typeof PrCreatedHistoryEvent>;

export const PrStateChangeHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("pr_state_change"),
  prNumber: Schema.Number,
  mergeStateStatus: Schema.NullOr(Schema.String),
  mergeable: Schema.NullOr(Schema.String),
});
export type PrStateChangeHistoryEvent = Schema.Schema.Type<typeof PrStateChangeHistoryEvent>;

export const PrMergedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("pr_merged"),
  prNumber: Schema.Number,
  mergedBy: Schema.NullOr(Schema.String),
  externally: Schema.Boolean,
});
export type PrMergedHistoryEvent = Schema.Schema.Type<typeof PrMergedHistoryEvent>;

export const ContainerSpawnedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("container_spawned"),
  name: Schema.String,
  image: Schema.String,
  role: Schema.Literal("builder", "fixer"),
});
export type ContainerSpawnedHistoryEvent = Schema.Schema.Type<typeof ContainerSpawnedHistoryEvent>;

export const ContainerExitedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("container_exited"),
  name: Schema.String,
  exitCode: Schema.NullOr(Schema.Number),
  signal: Schema.NullOr(Schema.String),
});
export type ContainerExitedHistoryEvent = Schema.Schema.Type<typeof ContainerExitedHistoryEvent>;

/**
 * CLA-398: records which build runner a task's container was dispatched to once
 * orca execution moved off-host (`ORCA_RUNTIME=remote`). The remote runtime
 * picks an ONLINE runner from the pool per spawn and stamps this entry so a
 * single `select history from orca_tasks where linear_issue_identifier = X`
 * reproduces the runner⇄task assignment for operator observability. `runnerId`
 * / `runnerName` are the `orca_runners` row identity; `containerName` is the
 * deterministic `orca-{role}-{id}-{attempt}` name the snapshot/reaper join on.
 *
 * @since 1.0.0
 */
export const RunnerAssignedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("runner_assigned"),
  runnerId: Schema.String,
  runnerName: Schema.String,
  containerName: Schema.String,
});
export type RunnerAssignedHistoryEvent = Schema.Schema.Type<typeof RunnerAssignedHistoryEvent>;

export const LinearStatePushHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("linear_state_push"),
  from: Schema.NullOr(Schema.String),
  to: Schema.String,
  ok: Schema.Boolean,
  error: Schema.NullOr(Schema.String),
});
export type LinearStatePushHistoryEvent = Schema.Schema.Type<typeof LinearStatePushHistoryEvent>;

/**
 * INF-594: records a checker/fixer summary comment mirrored onto the Linear
 * issue (in addition to the existing PR comment). `source` identifies which
 * gate's summary was posted; `ok`/`error` capture the best-effort outcome so a
 * single history read shows whether the operator-visible Linear comment landed.
 *
 * INF-761: `source` gained `"runner_assignment"` for the best-effort comment
 * posted when a builder/fixer container is placed on a runner (naming the
 * runner, container, and attempt) — the same audit shape covers it.
 *
 * @since 1.0.0
 */
export const LinearCommentPostedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("linear_comment_posted"),
  source: Schema.Literal("completeness", "review", "fixer", "runner_assignment"),
  ok: Schema.Boolean,
  error: Schema.NullOr(Schema.String),
});
export type LinearCommentPostedHistoryEvent = Schema.Schema.Type<typeof LinearCommentPostedHistoryEvent>;

/**
 * OL-65: records a Linear write the projection seam DECLINED to make because
 * the task carries no `linear_issue_id` (a brief-fed / run-fed task).
 *
 * It is the null-path counterpart of {@link LinearStatePushHistoryEvent} and
 * {@link LinearCommentPostedHistoryEvent} — exactly one of the three is
 * appended per attempted write, so a task's history explains why Linear is
 * empty instead of saying nothing at all. `op` names which write was skipped;
 * `detail` carries the target state name (`state_push`) or the comment source
 * (`comment` / `runner_assignment_comment`), or `null` when there is nothing
 * useful to say.
 *
 * @since 1.0.0
 */
export const LinearProjectionSkippedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("linear_projection_skipped"),
  op: Schema.Literal("state_push", "comment", "runner_assignment_comment"),
  detail: Schema.NullOr(Schema.String),
});
export type LinearProjectionSkippedHistoryEvent = Schema.Schema.Type<typeof LinearProjectionSkippedHistoryEvent>;

export const LinearWebhookReceivedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("linear_webhook_received"),
  eventType: Schema.String,
  source: Schema.Literal("webhook", "sweep"),
});
export type LinearWebhookReceivedHistoryEvent = Schema.Schema.Type<
  typeof LinearWebhookReceivedHistoryEvent
>;

export const ReconcilerLeftHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("reconciler_left"),
  reason: Schema.String,
});
export type ReconcilerLeftHistoryEvent = Schema.Schema.Type<typeof ReconcilerLeftHistoryEvent>;

export const ErrorHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("error"),
  source: Schema.String,
  message: Schema.String,
  stack: Schema.NullOr(Schema.String),
});
export type ErrorHistoryEvent = Schema.Schema.Type<typeof ErrorHistoryEvent>;

/**
 * INF-721: records why the state-aware reaper policy spared or killed a task's
 * container early. Emitted by the reaper/reconciler (`@fm/api`) once per policy
 * decision. `status`/`ageMs` capture the observed agent state at decision time;
 * `action` is the outcome the policy chose. The `status` literal is defined
 * inline here (not imported from `@fm/api`) — the wire-side `AgentStatus` in
 * `packages/api/src/rpc/orca-runner.schemas.ts` intentionally mirrors it and the
 * two must not be cross-imported (dependency direction is api→db).
 *
 * @since 1.0.0
 */
export const AgentStatePolicyHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("agent_state_policy"),
  status: Schema.Literal("working", "idle", "blocked", "done", "unknown"),
  ageMs: Schema.Number,
  action: Schema.Literal("spared", "reaped_early", "deferred_hard_kill", "ceiling_reached"),
});
export type AgentStatePolicyHistoryEvent = Schema.Schema.Type<typeof AgentStatePolicyHistoryEvent>;

/**
 * INF-760: records that a fixer / gate-checker container spawn deferred because
 * every eligible runner was at capacity (or none was ONLINE) at spawn time.
 * Emitted ONCE per wait episode by the `@fm/api` dispatch paths so a capacity
 * wait is visible in the per-task forensic timeline instead of the task
 * silently sitting in FIXING / `*_CHECKING` with a stale `phase_entered_at`.
 * The queryable cross-task counterpart is an `orca_task_events` row with
 * `event_type = 'dispatch_deferred'` (the vocabulary INF-752 shipped, mirroring
 * INF-559's builder-path deferral stamp). `phase` names the phase the task is
 * parked in; `gate` names the gate whose spawn deferred (null when none);
 * `reason` carries the `runner-at-capacity` cause.
 *
 * @since 1.0.0
 */
export const DispatchDeferredHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("dispatch_deferred"),
  phase: OrcaTaskPhase,
  gate: Schema.NullOr(OrcaTaskGate),
  reason: Schema.String,
});
export type DispatchDeferredHistoryEvent = Schema.Schema.Type<typeof DispatchDeferredHistoryEvent>;

/**
 * INF-762: records that a builder/fixer spawn was REFUSED because a re-read of
 * the task row showed it was no longer eligible for that spawn — a replayed /
 * duplicate dispatch reaching the spawn seam after the build had already
 * finished (phase past BUILDING / a PR was created) or while a build was still
 * in flight (a live container in a runner snapshot, or an active dispatch-ledger
 * record for the task). `role` is the spawn that was refused; `reason` names
 * which eligibility check failed and the observed state (e.g. the stale phase),
 * so a single `select history from orca_tasks` shows exactly why the stale
 * dispatch was fenced off. No new `orca_task_events` literal is added — the
 * per-row history is the forensic record for this fence.
 *
 * @since 1.0.0
 */
export const StaleDispatchRefusedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("stale_dispatch_refused"),
  role: Schema.Literal("builder", "fixer"),
  reason: Schema.String,
});
export type StaleDispatchRefusedHistoryEvent = Schema.Schema.Type<typeof StaleDispatchRefusedHistoryEvent>;

/**
 * INF-750: records one merge auto-heal attempt (the plain-git self-heal that
 * runs when a PR reads `mergeStateStatus=DIRTY`). `outcome` is what the attempt
 * resolved to:
 *   - `"healed"` — the branch was merged with its base and pushed; `headSha` is
 *     the PR head SHA observed at the start of the heal (used next tick to catch
 *     a silent no-op heal — same head + still DIRTY ⇒ the push changed nothing).
 *   - `"conflict"` — a genuine, unresolvable merge conflict; a fixer was
 *     dispatched.
 *   - `"tooling-error"` — a tooling failure (auth/network/identity/missing
 *     branch). The consecutive count of these drives the escalation-to-fixer
 *     safety net so a persistent tooling error can never loop forever as a fake
 *     success (the 8/24 silent-no-op incident).
 *
 * OL-101: a `"conflict"` event additionally carries `conflictedFiles` — the
 * `git diff --name-only --diff-filter=U` paths captured inside the heal clone
 * before it is removed. PATHS ONLY, never hunk content: the event is rendered in
 * the admin UI and the diff itself is bounded, unstructured, and goes to the
 * fixer prompt instead. `Schema.optional` (not `NullOr`) so every history row
 * persisted before OL-101 — and every `"healed"` / `"tooling-error"` event,
 * which has no file list — still decodes unchanged; no migration is needed
 * because `history` is jsonb.
 *
 * @since 1.0.0
 */
export const MergeAutohealHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("merge_autoheal"),
  outcome: Schema.Literal("healed", "conflict", "tooling-error"),
  prNumber: Schema.Number,
  headSha: Schema.NullOr(Schema.String),
  conflictedFiles: Schema.optional(Schema.Array(Schema.String)),
});
export type MergeAutohealHistoryEvent = Schema.Schema.Type<typeof MergeAutohealHistoryEvent>;

/**
 * INF-794: records one zombie hard-kill attempt and its post-kill verification.
 * Emitted by the reconciler ({@link OrcaReconciler}) on every hard-kill tick so
 * the "kill reported success but the container kept running" loop is visible in
 * the per-task forensic timeline instead of only in the transient `left` reason.
 *
 *   - `action` is which primitive was issued this tick: `"kill"` for the normal
 *     stop+rm path, `"force-remove"` for the bounded escalation (`docker rm -f`)
 *     that fires after {@link countConsecutiveHardKillFailures} verified survivals.
 *   - `stillRunning` is the post-kill liveness probe result — `true` is the
 *     no-op-kill smoking gun.
 *   - `containerId` is the runner-reported docker id the kill actually resolved
 *     (null when the runner matched nothing — itself a diagnostic that the kill
 *     no-op'd because it resolved no container), and `detail` carries the
 *     docker CLI exit/stderr detail the runner surfaced (null on a clean kill).
 *     Together they pinpoint the no-op mechanism on the next occurrence.
 *
 * A force-remove event with `stillRunning: true` is also the alerted-once marker:
 * the operator alert is emitted on the tick that records it, and subsequent ticks
 * detect the event and stop re-issuing kills instead of re-alerting.
 *
 * @since 1.0.0
 */
export const HardKillHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("hard_kill"),
  action: Schema.Literal("kill", "force-remove"),
  stillRunning: Schema.Boolean,
  containerId: Schema.NullOr(Schema.String),
  detail: Schema.NullOr(Schema.String),
});
/**
 * Decoded type of {@link HardKillHistoryEvent}.
 *
 * @since 1.0.0
 */
export type HardKillHistoryEvent = Schema.Schema.Type<typeof HardKillHistoryEvent>;

/**
 * OL-100: records that the CI gate observed a FAILED status context which the
 * repo's `.github/required-status-checks.txt` manifest does not list as
 * required, and therefore did NOT treat as a gating failure — no phase
 * transition, no fixer. `context` is the live status-context name verbatim
 * (e.g. `Effect Diagnostics (api)`), so the forensic timeline names the check
 * that was ignored rather than only recording a silence.
 *
 * @since 1.0.0
 */
export const CiNonrequiredFailureIgnoredHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("ci_nonrequired_failure_ignored"),
  context: Schema.String,
});
/**
 * Decoded type of {@link CiNonrequiredFailureIgnoredHistoryEvent}.
 *
 * @since 1.0.0
 */
export type CiNonrequiredFailureIgnoredHistoryEvent = Schema.Schema.Type<
  typeof CiNonrequiredFailureIgnoredHistoryEvent
>;

/**
 * OL-100: records one `rerun-failed-jobs` dispatch against a workflow run whose
 * head sha showed a CI failure with NO failing job — the signature of a
 * CANCELLED / superseded / runner-lost run (infra, not code). This event IS the
 * once-per-head-sha guard: before re-running, the gate scans `history` for a
 * `ci_rerun` whose `headSha` equals the current head sha and does nothing when
 * one exists. Deliberately NOT a column — see OL-100; the history array is
 * append-only on the row, so the guard needs no migration.
 *
 * `jobName` is the failing/cancelled job the rerun targeted when one was
 * identifiable, `null` when the run reported none (the usual cancelled-run
 * case). `Schema.optional` on the fields older rows cannot carry keeps every
 * persisted history array decodable.
 *
 * @since 1.0.0
 */
export const CiRerunHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("ci_rerun"),
  runId: Schema.Number,
  headSha: Schema.String,
  jobName: Schema.optional(Schema.NullOr(Schema.String)),
});
/**
 * Decoded type of {@link CiRerunHistoryEvent}.
 *
 * @since 1.0.0
 */
export type CiRerunHistoryEvent = Schema.Schema.Type<typeof CiRerunHistoryEvent>;

/**
 * OL-129: records that the liveness rule declared a builder / fixer container
 * LOST — the task's own container is absent from the runner snapshot and the
 * heartbeat evidence agrees. It carries the evidence the decision was made on,
 * so a single `select history from orca_tasks` reproduces the call without the
 * snapshot that produced it:
 *
 *   - `lastHeartbeatAt` is the last accepted beat (`builder_heartbeat_at`), or
 *     `null` when no beat was ever accepted for this attempt — a container that
 *     died before its first beat is the common lost shape.
 *   - `snapshotReportedAt` is the moment of the runner snapshot that showed the
 *     container absent, so the absence is pinned to an observation, not to the
 *     tick that read it.
 *   - `containerName` is the deterministic `orca-{role}-{id}-{attempt}` name the
 *     snapshot / kill helper join on.
 *   - `detectedBy` names which recovery path made the call.
 *
 * The queryable cross-task counterpart is an `orca_task_events` row with
 * `event_type = 'builder_lost'`. This model only defines the vocabulary; the
 * recovery routine writes it.
 *
 * @since 1.0.0
 */
export const BuilderLostHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("builder_lost"),
  role: Schema.Literal("builder", "fixer"),
  containerName: Schema.String,
  lastHeartbeatAt: Schema.NullOr(Schema.String),
  snapshotReportedAt: Schema.String,
  detectedBy: Schema.Literal("dispatch-reconciler", "building-recovery", "fixer-recovery"),
});
/**
 * Decoded type of {@link BuilderLostHistoryEvent}.
 *
 * @since 1.0.0
 */
export type BuilderLostHistoryEvent = Schema.Schema.Type<typeof BuilderLostHistoryEvent>;

/**
 * OL-129: records that the kill helper force-removed a container that was still
 * PRESENT — the duplicate half of a lost-builder recovery, where a re-spawn must
 * not race a container that is still there. `containerName` is the container the
 * kill targeted; `containerId` is the runtime's reported docker id, `null` when
 * the runner matched nothing (itself the diagnostic that the kill resolved no
 * container), and `runnerId` is the `orca_runners` row the kill was issued to,
 * `null` when the container was not attributable to a runner.
 *
 * The queryable cross-task counterpart is an `orca_task_events` row with
 * `event_type = 'duplicate_killed'`. Distinct from {@link HardKillHistoryEvent},
 * which records the reconciler's zombie hard-kill ladder and its post-kill
 * liveness probe; this one records a recovery-path duplicate removal.
 *
 * @since 1.0.0
 */
export const DuplicateKilledHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("duplicate_killed"),
  containerName: Schema.String,
  containerId: Schema.NullOr(Schema.String),
  runnerId: Schema.NullOr(Schema.String),
});
/**
 * Decoded type of {@link DuplicateKilledHistoryEvent}.
 *
 * @since 1.0.0
 */
export type DuplicateKilledHistoryEvent = Schema.Schema.Type<typeof DuplicateKilledHistoryEvent>;

/**
 * OL-129: records that a recovery path ADOPTED an already-open PR instead of
 * re-spawning a builder — the lost container had in fact pushed its work before
 * it went away, so the task resumes from that PR. `prNumber` / `headRef` are the
 * adopted PR's identity (the same pair {@link PrCreatedHistoryEvent} carries, so
 * the two entries line up in the timeline), and `adoptedBy` names the path.
 *
 * The queryable cross-task counterpart is an `orca_task_events` row with
 * `event_type = 'pr_adopted'`.
 *
 * @since 1.0.0
 */
export const PrAdoptedHistoryEvent = Schema.Struct({
  ts: Schema.String,
  kind: Schema.Literal("pr_adopted"),
  prNumber: Schema.Number,
  headRef: Schema.String,
  adoptedBy: Schema.Literal("dispatch-reconciler", "building-recovery", "fixer-recovery"),
});
/**
 * Decoded type of {@link PrAdoptedHistoryEvent}.
 *
 * @since 1.0.0
 */
export type PrAdoptedHistoryEvent = Schema.Schema.Type<typeof PrAdoptedHistoryEvent>;

/**
 * OL-159: records one decision of the recovery playbook against this task — the
 * per-task forensic half of the `recovery_applied` / `recovery_escalated` /
 * `recovery_skipped_budget` {@link OrcaTaskEventType} literals:
 *
 *   - `action` is which decision was taken: `"applied"` ran the recovery,
 *     `"escalated"` handed it off instead of retrying (the recovery repeated
 *     past its per-task ceiling), `"skipped_budget"` declined it because the
 *     task had already spent its recovery budget for the window.
 *   - `name` is the recovery's stable name (e.g. `respawn-lost-builder`). It is
 *     written verbatim as the `reason` of the paired `orca_task_events` row, so
 *     `summarizeByReasonSince` groups by exactly this string.
 *   - `summary` is the operator-readable one-liner for the timeline.
 *   - `at` is the ISO moment the decision was taken.
 *
 * This model only defines the vocabulary; the playbook writes it.
 *
 * @since 1.0.0
 */
export const RecoveryHistoryEvent = Schema.Struct({
  kind: Schema.Literal("recovery"),
  action: Schema.Literal("applied", "escalated", "skipped_budget"),
  name: Schema.String,
  summary: Schema.String,
  at: Schema.String,
});
/**
 * Decoded type of {@link RecoveryHistoryEvent}.
 *
 * @since 1.0.0
 */
export type RecoveryHistoryEvent = Schema.Schema.Type<typeof RecoveryHistoryEvent>;

/**
 * Discriminated union of every history event kind. New event kinds extend
 * this union and a paired entry must be appended at the producer site (see
 * the wired sites in OrcaStateMachine, OrcaTaskDispatcher, OrcaReconciler,
 * OrcaContainerReaper, OrcaProjectScheduler, webhooks/orca, and
 * cancel-canceled-issue).
 *
 * @since 1.0.0
 */
export const OrcaTaskHistoryEvent = Schema.Union(
  PhaseTransitionHistoryEvent,
  WorkflowDispatchHistoryEvent,
  WorkflowCompletedHistoryEvent,
  CheckRunHistoryEvent,
  PrCreatedHistoryEvent,
  PrStateChangeHistoryEvent,
  PrMergedHistoryEvent,
  ContainerSpawnedHistoryEvent,
  ContainerExitedHistoryEvent,
  RunnerAssignedHistoryEvent,
  LinearStatePushHistoryEvent,
  LinearCommentPostedHistoryEvent,
  LinearProjectionSkippedHistoryEvent,
  LinearWebhookReceivedHistoryEvent,
  ReconcilerLeftHistoryEvent,
  ErrorHistoryEvent,
  AgentStatePolicyHistoryEvent,
  DispatchDeferredHistoryEvent,
  StaleDispatchRefusedHistoryEvent,
  MergeAutohealHistoryEvent,
  HardKillHistoryEvent,
  CiNonrequiredFailureIgnoredHistoryEvent,
  CiRerunHistoryEvent,
  BuilderLostHistoryEvent,
  DuplicateKilledHistoryEvent,
  PrAdoptedHistoryEvent,
  RecoveryHistoryEvent,
);
export type OrcaTaskHistoryEvent = Schema.Schema.Type<typeof OrcaTaskHistoryEvent>;

/**
 * Hard cap on history entries retained per row. The accessor trims to the
 * most-recent N at append time. 200 is a generous bound — at the current
 * event rate (~30/task across the full lifecycle) it leaves 6x headroom
 * for fixer churn before older entries roll off.
 *
 * @since 1.0.0
 */
export const ORCA_TASK_HISTORY_CAP = 200;

/**
 * OrcaTask model tracking a unit of work from Linear issue to merged PR.
 *
 * @since 1.0.0
 */
export class OrcaTask extends Model.Class<OrcaTask>("OrcaTask")({
  id: Model.Generated(Schema.String),

  /**
   * OL-64: `null` for a brief-fed task — one the outcome engine dispatched
   * from a brief rather than from a Linear issue. `linearIssueIdentifier`
   * below stays required and carries the run id for those rows, so it, not
   * this, is the identity every downstream consumer keys on.
   *
   * @since 1.0.0
   */
  linearIssueId: Schema.NullOr(Schema.String),
  linearIssueIdentifier: Schema.String,
  title: Schema.String,
  description: Schema.NullOr(Schema.String),
  repo: Schema.String,
  branch: Schema.NullOr(Schema.String),
  prNumber: Schema.NullOr(Schema.Number),

  agentCli: OrcaAgentCli,
  // Claude Code `--model` id for the builder/fixer container (see
  // orcaTasks.model in src/tables/orca-tasks.ts). Plain string — the set of
  // supported models is owned by the API-layer label resolver, not a DB enum.
  model: Schema.String,

  phase: OrcaTaskPhase,
  gate: Schema.NullOr(OrcaTaskGate),
  attempt: Schema.Number,
  maxAttempts: Schema.Number,
  failureContext: Schema.NullOr(Schema.String),
  attemptHistory: Schema.Array(AttemptHistoryEntry),
  history: Schema.Array(OrcaTaskHistoryEvent),
  completenessCheckRunId: Schema.NullOr(Schema.Number),
  reviewCheckRunId: Schema.NullOr(Schema.Number),
  fixerCheckRunId: Schema.NullOr(Schema.Number),
  completenessWorkflowRunId: Schema.NullOr(Schema.Number),
  reviewWorkflowRunId: Schema.NullOr(Schema.Number),

  // INF-708: lost-dispatch recovery bookkeeping. `gateDispatchPendingAt` is
  // stamped when a checking phase is entered and cleared once the gate
  // workflow run id is persisted; `gateRedispatchCount` caps lifetime
  // reconciler redispatches at 2. See src/tables/orca-tasks.ts.
  gateDispatchPendingAt: Schema.NullOr(Schema.DateTimeUtc),
  gateRedispatchCount: Schema.Number,

  /**
   * OL-128: when this task's CURRENT dispatch last proved it was alive (see
   * src/tables/orca-tasks.ts). Advanced only by the dispatch-version-fenced
   * `touchBuilderHeartbeat` accessor, so a superseded container's beat never
   * shows here. NULL until the dispatch's first accepted beat.
   *
   * @since 1.0.0
   */
  builderHeartbeatAt: Schema.NullOr(Schema.DateTimeUtc),

  // INF-744: attempt-neutral merge-conflict self-heal counter (see
  // src/tables/orca-tasks.ts). Incremented instead of `attempt` when a DIRTY
  // PR is promoted into the fix loop at gate=merge, capped at
  // MAX_MERGE_FIX_ATTEMPTS. Default 0 for pre-migration rows.
  mergeFixCount: Schema.Number,

  // OL-51: head sha each gate recorded a PASS for (see src/tables/orca-tasks.ts).
  // Compared against the live PR head before any `mergeStateStatus`-driven merge
  // retry, so a head that no gate has judged is never merged. NULL until the
  // gate passes once.
  completenessGateHeadSha: Schema.NullOr(Schema.String),
  reviewGateHeadSha: Schema.NullOr(Schema.String),

  // OL-49: CI failure-context wait stamp (see src/tables/orca-tasks.ts).
  // Non-NULL while a CI failure has been observed for the head sha but no
  // failing JOB is visible yet — the fixer dispatch waits instead of spawning
  // blind. NULL for every pre-migration row and whenever no wait is in flight.
  ciContextPendingAt: Schema.NullOr(Schema.DateTimeUtc),

  orcaProjectId: Schema.NullOr(Schema.String),

  /**
   * OL-60: the customer that owns this task; `null` = unowned legacy row that
   * predates the backfill. Read by the placement label and the credential
   * lease. Distinct from the outcome-engine `runCustomerId` link (OL-81),
   * which is NULL on every ordinary orca task.
   *
   * @since 1.0.0
   */
  customerId: Schema.NullOr(Schema.String),

  /**
   * OL-81: the task→run link, as a PAIR. Both non-NULL means this build task
   * is an outcome-engine run — `runId` addresses the `orcaloops.runs` row and
   * `runCustomerId` is what scopes every lookup of it. Both NULL on every
   * ordinary orca task. Never resolved from the Linear issue id, which OL-64
   * makes nullable.
   *
   * @since 1.0.0
   */
  runId: Schema.NullOr(Schema.String),
  runCustomerId: Schema.NullOr(Schema.String),

  /**
   * OL-64: a brief-fed task's INPUT. `brief` is the prose ask and
   * `acceptance` the criteria list; both NULL on every issue-fed row. Stored
   * verbatim so the builder container receives them as `ORCA_BRIEF` /
   * `ORCA_ACCEPTANCE`, while the rendered form also lands in `description`
   * for the completeness gate.
   *
   * @since 1.0.0
   */
  brief: Schema.NullOr(Schema.String),
  acceptance: Schema.NullOr(Schema.Array(Schema.String)),

  dispatchDeferredReason: Schema.NullOr(Schema.String),
  dispatchDeferredAt: Schema.NullOr(Schema.DateTimeUtc),

  dispatchVersion: Schema.Number,

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
  phaseEnteredAt: Model.DateTimeUpdateFromDate,
}) {}
